import React, { useEffect } from 'react';
import { Flex, Box, Text, Tooltip } from '@mantine/core';
import ReportSectionCommentCounter from '../ReportComments/ReportSectionCommentCounter';

export function TableSections({
  sections,
  condensedSections,
  onNavigateToSection,
  isExpanded,
  activeSection,
  funds,
  isViewer = false,
  sectionErrorScope = {}
}) {
  const fundId = Array.isArray(funds) && funds.length ? funds[0]?.fund_id : null;

  const checkError = sectionId => {
    if (isViewer) {
      return false;
    }

    const {
      tableKey,
      mode = 'all',
      fields = [],
      rowTypes = null
    } = sectionErrorScope[sectionId] || { tableKey: sectionId };

    const ownsField = field => {
      if (mode === 'include') return fields.includes(field);
      if (mode === 'exclude') return !fields.includes(field);
      return true;
    };
    const ownsRow = row => !rowTypes || rowTypes.includes(row?.transaction_type);
    const tableKeys = Array.isArray(tableKey) ? tableKey : [tableKey];

    for (let k = 0; k < funds.length; k++) {
      const fund = funds[k];

      for (let t = 0; t < tableKeys.length; t++) {
        const values = fund?.fund_data?.[tableKeys[t]] || [];
        const requiredFields = fund?.required_tables?.[tableKeys[t]]?.field_names || [];

        for (let i = 0; i < values.length; i++) {
          const row = values[i];
          if (!ownsRow(row)) {
            continue;
          }

          const rowErrors = row?.errors;
          if (rowErrors) {
            if (mode === 'all') {
              return true;
            }
            const hasOwnedError = Object.keys(rowErrors).some(field =>
              field === 'general' ? mode === 'exclude' : ownsField(field)
            );
            if (hasOwnedError) {
              return true;
            }
          }

          for (let j = 0; j < requiredFields.length; j++) {
            const field = requiredFields[j];
            if (ownsField(field) && !row[field]) {
              return true;
            }
          }
        }
      }
    }

    return false;
  };

  useEffect(() => {
    const element = document.getElementById(`${activeSection}-button`);
    const viewport = document.getElementById('scrollable-sidebar');
    if (!element || !viewport) return;

    const elementRect = element.getBoundingClientRect();
    const viewportRect = viewport.getBoundingClientRect();
    const offset =
      elementRect.top - viewportRect.top - viewport.clientHeight / 2 + element.clientHeight / 2;

    viewport.scrollTo({
      top: viewport.scrollTop + offset,
      behavior: 'smooth'
    });
  }, [activeSection]);

  return (
    <Flex id="report-table-sections-root" direction="column" w="100%" gap="4px">
      {Object.keys(sections).map(id => (
        <Tooltip
          label={sections[id]}
          maw="321px"
          multiline
          bg="gray.6"
          fz="12px"
          lh="18px"
          p="8px"
          key={id}
        >
          <Box
            pos="relative"
            component="button"
            p="6px 8px"
            fz="14px"
            lh="20px"
            sx={() => ({
              textAlign: 'left',
              borderRadius: '4px',
              display: 'flex',
              overflow: isExpanded ? 'hidden' : 'visible',
              maxHeight: isExpanded ? '40px' : 'none',
              background:
                checkError(id) && activeSection === id
                  ? '#E7F5FF'
                  : activeSection === id
                    ? '#E7F5FF'
                    : '#fff',
              color: checkError(id) ? '#FA5252' : '#228BE6'
            })}
            onClick={() => onNavigateToSection(id)}
            id={`${id}-button`}
          >
            <Box
              id={`report-table-section-shortcut-${id}`}
              sx={() => ({
                minWidth: isExpanded ? 0 : '28px',
                minHeight: isExpanded ? 0 : '28px',
                width: isExpanded ? 0 : '28px',
                height: isExpanded ? 0 : '28px',
                borderRadius: '5px',
                border: checkError(id) ? '1px solid #fa5252' : '1px solid #228be6',
                display: 'flex',
                transition: '0.1s ease-in-out width',
                opacity: isExpanded ? 0 : 1,
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '12px',
                fontWeight: '500'
              })}
            >
              {condensedSections?.[id]}
            </Box>
            <Text
              id={`report-table-section-label-${id}`}
              fz="14px"
              lh="20px"
              sx={() => ({
                transition: isExpanded ? '2s ease-in-out opacity' : '0.2s ease-in-out opacity',
                opacity: isExpanded ? 1 : 0,
                width: isExpanded ? 'auto' : 0,
                height: isExpanded ? '20px' : 0,
                overflow: 'hidden'
              })}
            >
              {sections[id]}
            </Text>
            {id && (
              <ReportSectionCommentCounter
                fundId={fundId}
                tableName={id}
                isMultiFunds={Array.isArray(funds) && funds.length > 1}
                collapsed={!isExpanded}
              />
            )}
          </Box>
        </Tooltip>
      ))}
    </Flex>
  );
}
